import { useCallback, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, Volume2, VolumeX } from 'lucide-react'
import { GamePlayer, getGameEntry } from '@quebon/games'
import { useAuthStore } from '@/store/authStore'
import { Button3D } from '@/components/learner/ui/Button3D'
import { Mascot } from '@/components/learner/ui/Mascot'
import { useSubmitGameScore } from '@/hooks/learner/useSubmitGameScore'
import { learnerGamesApi } from '@/api/learnerGames'
import { createPlaygroundGameApi, type PlaygroundApiOperation } from '@/lib/playground/createGameApi'

export default function PlaygroundGame() {
  const { gameKey } = useParams<{ gameKey: string }>()
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('learner')
  const user = useAuthStore((s) => s.user)
  const { mutateAsync: submitScore } = useSubmitGameScore()

  const [isSoundOn, setIsSoundOn] = useState(true)
  const [apiError, setApiError] = useState<PlaygroundApiOperation | null>(null)

  const entry = gameKey ? getGameEntry(gameKey) : undefined
  const access = useQuery({
    queryKey: ['learner-games', 'access', gameKey],
    queryFn: async () => {
      const response = await learnerGamesApi.access(gameKey as string)
      return response.data.data
    },
    enabled: Boolean(entry && gameKey),
    retry: false,
  })

  const handleBack = useCallback(() => navigate('/my/playground'), [navigate])

  const reportApiError = useCallback((operation: PlaygroundApiOperation) => setApiError(operation), [])
  const gameApi = useMemo(
    () => createPlaygroundGameApi({ gameKey: gameKey ?? '', submitScore, onError: reportApiError }),
    [gameKey, submitScore, reportApiError],
  )

  if (!entry || !gameKey) {
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-5 bg-lc-gray-900 px-4 text-center">
        <Mascot name="ddami" shape="roundbox" size={120} decorative />
        <p className="text-lc-body-2 text-white/80">
          {t('playground.unknownGame')}
        </p>
        <Button3D variant="lightblue" size="M" onClick={handleBack}>
          {t('playground.backToList')}
        </Button3D>
      </div>
    )
  }

  if (access.isPending) {
    return <LCGameLoading />
  }

  if (access.isError || !access.data?.accessible) {
    const status = (access.error as { response?: { status?: number } } | null)?.response?.status
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-5 bg-lc-gray-900 px-4 text-center">
        <Mascot name="ddami" shape="roundbox" size={120} decorative />
        <p className="text-lc-body-2 text-white/80" role="alert">
          {status === 403
            ? t('playground.game.accessLocked')
            : t('playground.game.accessUnavailable')}
        </p>
        <Button3D variant="lightblue" size="M" onClick={handleBack}>
          {t('playground.backToList')}
        </Button3D>
      </div>
    )
  }

  const title = i18n.language === 'en' ? entry.en.name : entry.ko.name

  return (
    <div className="relative flex h-screen flex-col overflow-hidden bg-lc-gray-900">
      <header
        role="banner"
        className="flex h-14 flex-shrink-0 items-center justify-between border-b border-lc-gray-800 bg-lc-gray-900 px-4"
        style={{ zIndex: 9999 }}
      >
        <button
          type="button"
          onClick={handleBack}
          aria-label={t('playground.game.back')}
          className="inline-flex size-10 items-center justify-center rounded-full bg-lc-gray-800 text-white transition-colors hover:bg-lc-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-lightblue-900"
        >
          <ArrowLeft className="size-5" />
        </button>

        <h1 className="max-w-[60%] truncate text-lc-body-1 font-semibold text-white">
          {title}
        </h1>

        <button
          type="button"
          onClick={() => setIsSoundOn((v) => !v)}
          aria-label={
            isSoundOn ? t('playground.game.mute') : t('playground.game.unmute')
          }
          aria-pressed={isSoundOn}
          className="inline-flex size-10 items-center justify-center rounded-full bg-lc-gray-800 transition-colors hover:bg-lc-gray-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-lightblue-900"
        >
          {isSoundOn ? (
            <Volume2 className="size-5 text-lc-orange-900" />
          ) : (
            <VolumeX className="size-5 text-lc-gray-500" />
          )}
        </button>
      </header>

      {apiError && (
        <p role="alert" className="bg-red-950 px-4 py-3 text-sm text-white">
          {t(`playground.game.apiErrors.${apiError}`)}
        </p>
      )}

      <div className="relative flex-1 overflow-hidden">
        <GamePlayer
          gameKey={gameKey}
          user={
            user
              ? {
                  nickname: user.fullName || user.email || 'Learner',
                  id: String(user.id),
                }
              : null
          }
          isSoundOn={isSoundOn}
          api={gameApi}
          onClose={handleBack}
          loadingFallback={<LCGameLoading />}
        />
      </div>
    </div>
  )
}

function LCGameLoading() {
  const { t } = useTranslation('learner')
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="flex h-screen flex-col items-center justify-center gap-4 bg-lc-gray-900"
    >
      <Mascot
        name="queda"
        shape="circle"
        size={80}
        className="animate-bounce"
        decorative
      />
      <p className="text-lc-caption-1 text-white/80">
        {t('playground.game.loading')}
      </p>
    </div>
  )
}
